<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import type { ApiException, DashboardPermission } from "@/api-clients/dashboard-api";
import { useUserPreferencesStore } from "@/stores/userPreferencesStore";
import { useUserAuthorizationStore } from "@/stores/userAuthorizationStore";
import { useAccountStore } from "@/stores/accountStore";
import segment from "@/helpers/segment";
import { redirectToAuthentication } from "@/helpers/commonHelpers";

import HeaderComponent from "@/components/HeaderComponent.vue";
import CampaignBannerComponent from "@/components/campaign/CampaignBannerComponent.vue";
import LeftMenu from "@/components/LeftMenu.vue";
import AccessDenied from "@/views/AccessDenied.vue";
import HttpErrorView from "@/views/HttpErrorView.vue";

const route = useRoute();
const userPrefsStore = useUserPreferencesStore();
const userAuthorizationStore = useUserAuthorizationStore();
const accountStore = useAccountStore();

const { lastAuthorization, authorizationIsDone, authorizationInProgress, authorizationError, permissions } = storeToRefs(userAuthorizationStore);
const { currentAccount } = storeToRefs(accountStore);

const permissionError = computed(() => {
  if (!authorizationIsDone.value || authorizationInProgress.value || lastAuthorization.value == null) {
    return undefined;
  }

  if (lastAuthorization.value.isAccessRestricted) {
    return 'unavailable';
  }

  const requiredPermissions = (route.meta.requiredPermissions as DashboardPermission[]) ?? [];
  const userPermissions = permissions.value ?? [];
  if (requiredPermissions.length > 0 && !requiredPermissions.every(p => userPermissions.includes(p))) {
    return 'no-permission';
  }

  return undefined;
});

const leftMenuHidden = computed(() => route.meta.noLeftMenu || authorizationError.value || permissionError.value != null);

const isUserLoaded = computed(() => !currentAccount.value.isLoading && currentAccount.value.content);

onMounted(async () => {
  segment.identify();
  const refreshSuccessful = await refreshAuth();
  if (refreshSuccessful) {
    await accountStore.refresh();
  }
});

async function refreshAuth() {
  try {
    await userAuthorizationStore.refreshAuthorization();
    return true;
  } catch (error) {
    const { status } = error as ApiException;
    if (status === 401) {
      redirectToAuthentication(route.fullPath);
    }
    return false;
  }
}

watch(() => lastAuthorization.value,
  (newValue) => {
    if (newValue?.sessionIdHash) {
      userPrefsStore.setSessionIdHash(newValue.sessionIdHash);
    }
  });

watch(() => route.path,
  () => {
    refreshAuth();
  });
</script>

<template>
  <HeaderComponent v-if="isUserLoaded" />
  <CampaignBannerComponent v-if="isUserLoaded" />

  <div class="l-page__sidebar-layout">
    <div class="l-container" :class="{ 'l-container-900': leftMenuHidden }">
      <aside v-if="!leftMenuHidden" class="l-page__sidebar">
        <LeftMenu v-if="isUserLoaded" />
      </aside>
      <div class="l-page__main">
        <HttpErrorView v-if="authorizationError" :error-code="authorizationError.status"
          :error-description="authorizationError.message" />
        <AccessDenied v-else-if="permissionError" :type="permissionError" />
        <RouterView v-else />
      </div>
    </div>
  </div>
</template>

<style scoped>
.l-container-900 {
  max-width: 900px;
}
</style>
